
import Chart from "chart.js/auto";

import { ExportToCsv } from "export-to-csv";

import Vue from "vue";
import "bootstrap-table/dist/bootstrap-table.js";
import "bootstrap-table/dist/bootstrap-table.min.css";
import { AjaxGet, Alert, GetClient, GetParks, IsAdmin } from "../App.vue";

import CountryFilter from "../components/CountryFilter.vue";
import ClientFilter from "../components/ClientFilter.vue";
import { GetDateReportURL, GetStartedGameURL, GetTimeInParksURL, GetWonAtParkURL } from "../../../shared/Shared";
import ParkSite from "../../../shared/ParkSite";
import ParkDisplayName from "../../../shared/ParkDisplayName";
import { __values } from "tslib";

const AllParks: ParkDisplayName = { id: "All Parks", displayName: "All Parks" };

const dateRequest = 0;
const timeRequest = 1;
const wonRequest = 2;
const startedRequest = 3;

// Erik - added interface to represent data held in tabledata
interface Table {
  day: string;
  park: string;
  attend: string;
  est: number;
  client: string;
}

interface WinTable {
  client: string;
  park: string;
  game: string;
  plays: number;
  wins: number;
  ratio: string;
}

interface TimeTable {
  park: string;
  time: string;
  client: string;
}

class PortalViewChartsDataClass {
  public SelectedFilterCountry: string = "";
  public SelectedFilterCouncil: string = "";

  public ParkSitesDictionary: { [id: string]: ParkSite } = {};
  public ParkSitesList: ParkSite[] = [];

  public selectedPark = AllParks;
  public nodata = true;
  public timetabledata: TimeTable[] = [];
  public wintabledata: WinTable[] = [];

  public rawStartedData: string[][] = [];
  public rawWinData: string[][] = [];

  public tabledata: Table[] = [];
  public startDate = "";
  public endDate = "";

  public uniqueLocations: ParkDisplayName[] = [];
  public datesLineChart: Chart | null = null;
  public parksBarChart: Chart | null = null;
  public chartDates: string[] = [];
  public chartValues: number[] = [];
  public chartEstimateValues: number[] = [];
  public chartParkValues: number[] = [];
  public chartEstParkValues: number[] = [];

  public tableoptions = {
    search: false,
    showColumns: false,
    classes: "table table-striped table-sm",
  };
}

export default Vue.extend({
  name: "PortalViewCharts",
  data() {
    return new PortalViewChartsDataClass();
  },

  components: {
    CountryFilter,
    ClientFilter,
  },

  computed: {
    wincolumns: {
      get() {
        const result = [];

        if (IsAdmin()) {
          result.push({
            title: "Client",
            field: "client",
            sortable: true,
          });
        }

        result.push({
          title: "Park",
          field: "park",
          sortable: true,
        });

        result.push({
          title: "Game",
          field: "game",
          sortable: true,
        });

        result.push({
          title: "Plays",
          field: "plays",
          sortable: true,
        });

        result.push({
          title: "Wins",
          field: "wins",
          sortable: true,
        });

        result.push({
          title: "Win Ratio",
          field: "ratio",
          sortable: true,
        });

        return result;
      },
    },

    timetablecolumns: {
      get() {
        const result = [];

        if (IsAdmin()) {
          result.push({
            title: "Client",
            field: "client",
            sortable: true,
          });
        }

        result.push({
          title: "Park",
          field: "park",
          sortable: true,
        });

        result.push({
          title: "Total play time (Hours)",
          field: "time",
          sortable: true,
        });

        return result;
      },
    },

    tablecolumns: {
      get() {
        const result = [];
        result.push({
          title: "Date",
          field: "day",
          sortable: true,
        });

        if (IsAdmin()) {
          result.push({
            title: "Client",
            field: "client",
            sortable: true,
          });
        }

        result.push({
          title: "Park",
          field: "park",
          sortable: true,
        });

        result.push({
          title: "Activations",
          field: "attend",
          sortable: true,
        });

        result.push({
          title: "Est Visitors",
          field: "est",
          sortable: true,
        });
        return result;
      },
    },
  },

  created() {
    const yourDate = new Date();
    this.endDate = yourDate.toISOString().split("T")[0];
    yourDate.setFullYear(yourDate.getFullYear() - 1);
    this.startDate = yourDate.toISOString().split("T")[0];
    this.sendInitRequest();
  },

  methods: {
    IsAdmin() {
      return IsAdmin();
    },
    startExport() {
      const options = {
        fieldSeparator: ",",
        quoteStrings: '"',
        decimalSeparator: ".",
        showLabels: true,
        showTitle: true,
        title: "Park Data",
        useTextFile: false,
        useBom: true,
        useKeysAsHeaders: true,
      };

      const csvExporter = new ExportToCsv(options);
      csvExporter.generateCsv(this.tabledata);
    },

    // Filters the park and returns the display name, if any
    filterLocation(location: string): string {
      const park = this.ParkSitesDictionary[location];
      if (!park) {
        // Non existant park
        return "";
      }

      if (this.SelectedFilterCountry || this.SelectedFilterCouncil) {
        if (this.SelectedFilterCouncil && park.council !== this.SelectedFilterCouncil) {
          return "";
        }

        if (this.SelectedFilterCountry && park.country !== this.SelectedFilterCountry) {
          return "";
        }
      }

      if (this.selectedPark.id === location || this.selectedPark === AllParks) {
        if (park.displayName) {
          return park.displayName;
        }
        return location;
      }
      return "";
    },

    generatePromise(type: number): Promise<void> {
      return new Promise<void>((resolve, reject) => {
        let url: string = "";
        switch (type) {
          case dateRequest:
            url = GetDateReportURL(this.startDate, this.endDate);
            break;

          case timeRequest:
            url = GetTimeInParksURL(this.startDate, this.endDate);
            break;

          case wonRequest:
            url = GetWonAtParkURL(this.startDate, this.endDate);
            break;

          case startedRequest:
            url = GetStartedGameURL(this.startDate, this.endDate);
            break;
        }

        AjaxGet<string[][]>(url, true)
          .then((data) => {
            switch (type) {

              case dateRequest:
                this.setDateHTML(prepareV2Data(data));
                break;

              case timeRequest:
                this.setTimeDataHTML(prepareV2Count(data));
                break;

              case wonRequest:
                this.rawWinData = prepareV2Count(data);
                break;

              case startedRequest:
                this.rawStartedData = prepareV2Count(data);
                break;

            }

            resolve();
          })
          .catch(reject);
      });
    },

    // Merge the win and loss information
    processParkDict(parkDict: { [key: string]: WinTable }, data: string[][], isWonData: boolean) {

      // Start with the started game data
      for (const d of data) {

        const park = this.filterLocation(d[0].split("|")[0].trim());
        if (park) {

          const client = GetClient(d[0].split("|")[0], this.ParkSitesDictionary);
          const game = d[0].split("|")[1].split("_")[0].trim(); // Data bug with ocean rescue, make sure they're combined into one game
          const key = park + "_" + client + "_" + game;

          if (!parkDict[key]) {
            parkDict[key] = {
              park,
              client,
              game,
              plays: 0,
              wins: 0,
              ratio: ""
            };
          }

          const value = parseInt(d[1], 10);
          if (isWonData) {
            parkDict[key].wins = value;
          } else {
            parkDict[key].plays = value;
          }
        }

      }

    },

    populateTable() {
      const promises = [];
      promises.push(this.generatePromise(dateRequest));
      promises.push(this.generatePromise(timeRequest));
      promises.push(this.generatePromise(wonRequest));
      promises.push(this.generatePromise(startedRequest));


      Promise.all(promises)
        .then(() => {
          // Downloaded all promises

          const parkDict: { [key: string]: WinTable } = {};
          this.processParkDict(parkDict, this.rawStartedData, false);
          this.processParkDict(parkDict, this.rawWinData, true);

          this.wintabledata = [];
          for (const key of Object.keys(parkDict)) {

            const value = parkDict[key];
            let ratio = "????";
            if (value.plays > 0) {
              ratio = Math.round((value.wins * 100) / value.plays) + "%";
            }
            while (ratio.length < 4) {
              ratio = "0" + ratio;
            }

            this.wintabledata.push({
              park: value.park,
              client: value.client,
              game: value.game,
              wins: value.wins,
              plays: value.plays,
              ratio,
            });

          }
          this.$forceUpdate();
          this.nodata = false;

        })
        .catch((err) => Alert("Could not download analytics", err));
    },


    /*
      for (const d of data) {
        const parkName = this.filterLocation(d[0].split("|")[0].trim());

        if (parkName) {
          this.wintabledata.push({
            park: parkName,
            client: GetClient(d[0].split("|")[0], this.ParkSitesDictionary),
            game: d[0].split("|")[1].trim(),
            wins: parseInt(d[1], 10),
          });
        }
      }
      this.$forceUpdate();
      this.nodata = false;
    },
*/

    setTimeDataHTML(data: string[][]) {
      this.timetabledata = [];
      for (const d of data) {
        const parkName = this.filterLocation(d[0]);

        if (parkName) {
          this.timetabledata.push({
            park: parkName,
            client: GetClient(d[0], this.ParkSitesDictionary),
            time: (parseFloat(d[1]) / 60).toFixed(2),
          });
        }
      }
      this.$forceUpdate();
      this.nodata = false;
    },

    setDateHTML(data: string[][]) {
      this.chartDates = [];
      this.chartValues = [];
      this.chartEstimateValues = [];
      this.chartParkValues = [];
      this.chartEstParkValues = [];
      this.tabledata = [];

      this.uniqueLocations = [];
      this.uniqueLocations.push({ id: "", displayName: "" });

      for (const parkKey of Object.keys(this.ParkSitesDictionary)) {
        const park = this.ParkSitesDictionary[parkKey];
        if (!park) {
          continue;
        }
        if (this.SelectedFilterCouncil && park.council !== this.SelectedFilterCouncil) {
          continue;
        }
        if (this.SelectedFilterCountry && park.country !== this.SelectedFilterCountry) {
          continue;
        }

        this.uniqueLocations.push({
          id: parkKey,
          displayName: park.displayName ? park.displayName : parkKey,
        });
      }

      this.uniqueLocations.push({ id: "", displayName: "" });

      // eslint-disable-next-line
      for (const i of this.uniqueLocations) {
        this.chartParkValues.push(0);
        this.chartEstParkValues.push(0);
      }

      for (const d of data) {
        const displayName = this.filterLocation(d[0]);

        if (displayName) {
          this.tableContent(displayName, d[0], d[1], d[2]);
          this.chartContent(d[1], d[2]);
        }

        this.activationsByLocation(d[0], d[2]);
      }

      this.$forceUpdate();
      this.updateCharts();
    },

    chartContent(date: string, devices: string): void {
      this.chartDates.push(this.sliceDates(date));
      this.chartValues.push(parseInt(devices, 10));
      this.chartEstimateValues.push(Math.round(parseInt(devices, 10) * 2.5));
    },

    sliceDates(date: string): string {
      const str = [date.slice(0, 4), ".", date.slice(4, 6), ".", date.slice(6)].join("");
      return str;
    },

    tableContent(
      parkDisplayName: string,
      parkDataName: string,
      date: string,
      attend: string
    ): void {
      this.tabledata.push({
        day: this.sliceDates(date),
        park: parkDisplayName,
        attend,
        client: GetClient(parkDataName, this.ParkSitesDictionary),
        est: Math.round(parseFloat(attend) * 2.5),
      });
    },

    parkOptions(): ParkDisplayName[] {
      const result = [AllParks];

      for (const parkKey of Object.keys(this.ParkSitesDictionary)) {
        const park = this.ParkSitesDictionary[parkKey];

        if (this.SelectedFilterCouncil && park.council !== this.SelectedFilterCouncil) {
          continue;
        }
        if (this.SelectedFilterCountry && park.country !== this.SelectedFilterCountry) {
          continue;
        }

        result.push({
          id: parkKey,
          displayName: park.displayName ? park.displayName : parkKey,
        });
      }

      return result;
    },

    sendInitRequest() {
      GetParks()
        .then((data) => {
          this.ParkSitesDictionary = data;
          this.ParkSitesList = Object.values(data);
        })
        .then(() => {
          this.populateTable();
        });
    },

    activationsByLocation(location: string, devices: string) {
      const loc = this.uniqueLocations.findIndex((u) => u.id === location);
      this.chartParkValues[loc] += parseInt(devices, 10);
      this.chartEstParkValues[loc] += parseInt(devices, 10) * 2.5;
    },

    updateCharts() {
      // Chart control
      const linedata = {
        labels: this.chartDates,
        datasets: [
          {
            label: this.selectedPark.displayName + " Device Activations",
            backgroundColor: "rgba(94,180,229,0.05)",
            borderColor: "#5EB4E5",
            pointRadius: 5,
            lineTension: 0.15,
            data: this.chartValues,
          },
          {
            label: this.selectedPark.displayName + " Estimated Visitors",
            backgroundColor: "rgba(4,194,0,0.05)",
            borderColor: "#04c200",
            pointRadius: 5,
            lineTension: 0.15,
            data: this.chartEstimateValues,
          },
        ],
      };

      const labels: string[] = [];
      for (const location of this.uniqueLocations) {
        labels.push(location.displayName);
      }
      const bardata = {
        labels,
        datasets: [
          {
            label: "Total Park Device Activations",
            backgroundColor: "#5EB4E5",
            data: this.chartParkValues,
          },
          {
            label: "Total Estimated Park Visitors",
            backgroundColor: "#04c200",
            data: this.chartEstParkValues,
          },
        ],
      };

      // Global Config Options
      const options = {
        showScale: false,
        tooltipEvents: ["mousemove", "touchstart", "touchmove"],
        tooltipCornerRadius: 3,
        pointDot: true,
        pointDotRadius: 4,
        responsive: true,
        maintainAspectRatio: false,

        /*
    scales: {
      yAxes: [
        {
          ticks: {
            beginAtZero: true,
            min: 0,

            suggestedMax: Math.max.apply(Math, chartValues) + 1,

            userCallback: (label: any, index: any, labels: any) => {
              if (Math.floor(label) === label) {
                return label;
              }
            },
          },
        },
      ],
    },*/
      };

      if (this.datesLineChart && this.parksBarChart) {
        this.datesLineChart.config.data = linedata;
        this.parksBarChart.config.data = bardata;
        this.datesLineChart.update();
        this.parksBarChart.update();
      } else {
        const ctxL = (document.getElementById(
          // tslint:disable-next-line
          "datesLineChart"
        ) as HTMLCanvasElement).getContext("2d");

        // eslint-disable-next-line
        // @ts-ignore - Erik, ignore compatibility error with options
        this.datesLineChart = new Chart(ctxL, {
          type: "line",
          data: linedata,
          options,
        });

        const ctxS = (document.getElementById(
          // tslint:disable-next-line
          "parksBarChart"
        ) as HTMLCanvasElement).getContext("2d");

        if (ctxS) {
          this.parksBarChart = new Chart(ctxS, {
            type: "bar",
            data: bardata,
            options,
          });
        }
      }
    },
  },
});

function prepareV2Count(data: string[][]): string[][] {

  const processedData: { [park: string]: number } = {};

  for (const row of data) {
    if (!processedData[row[0]]) {
      processedData[row[0]] = 0;
    }
    processedData[row[0]] += parseInt(row[2], 10);
  }

  const output: string[][] = [];
  for (const parkKey of Object.keys(processedData).sort()) {
    output.push([
      parkKey,
      processedData[parkKey].toString()
    ]);
  }

  return output;

}

// Aggregate counts for park/date
function prepareV2Data(data: string[][]): string[][] {

  const processedData: { [park: string]: { [date: string]: number } } = {};

  for (const row of data) {
    if (!processedData[row[1]]) {
      processedData[row[1]] = {};
    }
    if (!processedData[row[1]][row[0]]) {
      processedData[row[1]][row[0]] = 0;
    }
    processedData[row[1]][row[0]] += parseInt(row[2], 10);
  }

  const output: string[][] = [];

  for (const dateKey of Object.keys(processedData).sort()) {
    for (const parkKey of Object.keys(processedData[dateKey]).sort()) {
      output.push([
        parkKey,
        dateKey,
        processedData[dateKey][parkKey].toString()
      ]);
    }
  }

  return output;

}
